import { NgIf } from '@angular/common';
import { AfterViewInit, Component, inject, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { merge, of as observableOf } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { ApiService } from '../../services/api/api.service';
import { TranslocoModule } from '@ngneat/transloco';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { Evidence } from '../../interfaces/Evidence';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { FileUploadModule, FileUploadControl, FileUploadValidators } from '@iplab/ngx-file-upload';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';
import { ConfirmationDialogComponent } from '../../common/confirm-dialog/confirn-dialog.component';
import { EvidenceResponse } from '../../interfaces/EvidenceResponse';
import { VulnerabilityAssessment } from '../../interfaces/VulnerabilityAssessment';
import { VulnerabilityAssessmentResponse } from '../../interfaces/VulnerabilityAssessmentResponse';
import { GlobalConstants } from '../../common/global-constants';

@Component({
    selector: 'app-v-view-evidences',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        NgIf,
        MatTooltipModule,
        TranslocoModule,
        MatProgressSpinnerModule,
        FormsModule,
        FileUploadModule,
        MatButtonModule,
        ReactiveFormsModule,
    ],
    templateUrl: './v-view-evidences.component.html',
    styleUrl: './v-view-evidences.component.scss',
})
export class VViewEvidencesComponent implements AfterViewInit {
    displayedColumns: string[] = ['label', 'filename', 'type', 'action'];
    data: Evidence[] = [];
    dataSource = new MatTableDataSource<Evidence>(this.data);
    totalItems = 0;
    pageSizes = [10, 20, 50, 1000];
    isLoadingResults = true;
    isRateLimitReached = false;

    addEvidenceForm: FormGroup;

    fileControl = new FileUploadControl(undefined, [
        FileUploadValidators.filesLimit(1),
        FileUploadValidators.accept(['.gif', '.jpg', '.png', '.zip']),
    ]);

    internal = GlobalConstants.internal;

    // Vulnerability Assessment Id
    vulnerabilityAssessmentId: string = '';

    // Vulnerability Assessment
    vulnerabilityAssessment: VulnerabilityAssessment;

    // Vulnerability Assessment Result Id
    vulnerabilityAssessmentResultId: string = '';

    // Evidence Id
    evidenceId: string = '';

    readonly dialog = inject(MatDialog);

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    // isToggled
    isToggled = false;

    //isWorking
    isWorking = false;

    constructor(
        public themeService: CustomizerSettingsService,
        private apiService: ApiService,
        private route: ActivatedRoute,
        private fb: FormBuilder
    ) {
        this.fileControl.setValue([]);
        this.addEvidenceForm = this.fb.group({
            label: ['', [Validators.required, Validators.maxLength(500)]],
            order: ['', []],
            file: this.fileControl,
        });
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for role name
     */
    get roleName(): string {
        return localStorage.getItem('roleName') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit() {
        this.route.params.subscribe((params) => {
            this.vulnerabilityAssessmentId =
                params['vulnerabilityAssessmentId'];

            this.vulnerabilityAssessmentResultId =
                params['vulnerabilityAssessmentResultId'];

            this.apiService
                .getVulnerabilityAssessmentById(
                    this.vulnerabilityAssessmentId,
                    this.roleName == GlobalConstants.internal
                )
                .subscribe((data: VulnerabilityAssessmentResponse) => {
                    this.vulnerabilityAssessment = data.vulnerabilityAssessment;
                });
        });
        this.isLoadingResults = false;
    }

    ngAfterViewInit() {
        // hook up sort/paginator once they exist
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

        merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                startWith({}),
                switchMap(() => {
                    this.isLoadingResults = true;
                    return this.apiService.getEvidenceByVulnerabilityResult(
                        this.vulnerabilityAssessmentResultId,
                        this.paginator.pageIndex,
                        this.paginator.pageSize,
                        this.sort.active,
                        this.sort.direction,
                        this.roleName != GlobalConstants.internal
                    ).pipe(catchError(() => observableOf(null)));
                }),
                map(data => {
                    this.isLoadingResults = false;
                    if (data === null) { return []; }
                    this.totalItems = data.totalElements;
                    return data.evidences;
                })
            )
            .subscribe(data => this.dataSource.data = data);
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // Search Filter
    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    // onKeydown
    onKeydown(event: Event) {
        //console.log(event);
        return false;
    }

    // Popup Trigger
    classApplied = false;
    toggleClass() {
        this.evidenceId = '';
        // clear file control before resetting form to avoid wrong type errors
        this.fileControl.clear();
        this.addEvidenceForm.reset({
            label: '',
            order: '',
            file: [] // FileUploadControl expects an array of File
        });
        this.addEvidenceForm.enable();
        this.classApplied = !this.classApplied;
    }

    // Método para limpiar el nombre del archivo y obtener la extensión
    cleanFileName(fileName: string): {
        cleanedName: string;
        extension: string;
    } {
        // Expresión regular para detectar UUID al final del archivo
        const uuidRegex =
            /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
        // Separar el nombre antes y después del UUID
        const parts = fileName.split(uuidRegex);

        if (parts.length > 1) {
            const cleanedNameWithPath = parts[0].trim(); // Nombre sin UUID
            const myArr = cleanedNameWithPath.split('/');
            const cleanedName = myArr[myArr.length - 1];
            const extension = cleanedNameWithPath.split('.').pop() || ''; // Extensión del archivo
            return { cleanedName, extension };
        } else {
            // Si no hay UUID, devolver el nombre original y su extensión
            const myArr = fileName.split('/');
            const cleanedName = myArr[myArr.length - 1];
            const extension = fileName.split('.').pop() || '';
            return { cleanedName, extension };
        }
    }

    // Add Evidence
    addEvidence() {
        if (this.fileControl.value.length == 0) {
            this.addEvidenceForm.get('file')?.setErrors({ required: true });
            return;
        }
        // Disable the form
        this.addEvidenceForm.disable();
        this.isWorking = true;
        // vulnerabilityAssessmentResult
        const vulnerabilityAssessmentResult: any = {
            id: this.vulnerabilityAssessmentResultId
        };
        // New Evidence
        const evidence = {
            label: this.addEvidenceForm.get('label')?.value,
            order: this.addEvidenceForm.get('order')?.value,
            filename: this.fileControl.value[0].name,
            vulnerabilityAssessmentResult: { id: this.vulnerabilityAssessmentResultId }
        };
        const formData = new FormData();
        // Append JSON data as a blob to maintain proper JSON format
        const jsonBlob = new Blob([JSON.stringify(evidence)], {
            type: 'application/json',
        });

        formData.append('evidence', jsonBlob);

        formData.append(
            'evidence_file',
            this.fileControl.value[0]
        );

        this.apiService.addEvidence(formData).subscribe({
            next: (value) => {
                this.classApplied = !this.classApplied;
                // ensure file control resets before form to avoid wrong argument type
                this.fileControl.clear();
                this.addEvidenceForm.reset({
                    label: '',
                    order: '',
                    file: []
                });
                this.addEvidenceForm.enable();
                this.isWorking = false;
                this.ngAfterViewInit();
            },
            error: (error) => {
                let errMsg: string = error;
                const dialogRef = this.dialog.open(AlertDialogComponent, {
                    data: {
                        title: 'ERROR',
                        message: errMsg,
                    },
                });
                this.addEvidenceForm.enable();
                this.isWorking = false;
            },
            complete: () => {
            }
        });
    }

    // Edit Evidence
    editEvidence() {
        // Disable the form
        this.addEvidenceForm.disable();
        this.isWorking = true;
        // edit Evidence
        let evidence;
        if (this.fileControl.value.length > 0 && this.fileControl.value[0] instanceof File && this.fileControl.value[0].name) {
            evidence = {
                label: this.addEvidenceForm.get('label')?.value,
                order: this.addEvidenceForm.get('order')?.value,
                filename: this.fileControl.value[0].name,
            };
        } else {
            evidence = {
                label: this.addEvidenceForm.get('label')?.value,
                order: this.addEvidenceForm.get('order')?.value,
            };
        }
        const formData = new FormData();
        // Append JSON data as a blob to maintain proper JSON format
        const jsonBlob = new Blob([JSON.stringify(evidence)], {
            type: 'application/json',
        });

        formData.append('evidence', jsonBlob);
        if (this.fileControl.value.length > 0) {
            formData.append(
                'evidence_file',
                this.fileControl.value[0]
            );
        }

        this.apiService.editEvidence(this.evidenceId, formData).subscribe({
            next: (value) => {
                this.evidenceId = '';
                this.classApplied = !this.classApplied;
                // ensure file control resets before form to avoid wrong argument type
                this.fileControl.clear();
                this.addEvidenceForm.reset({
                    label: '',
                    order: '',
                    file: []
                });
                this.addEvidenceForm.enable();
                this.isWorking = false;
                this.ngAfterViewInit();
            },
            error: (error) => {
                let errMsg: string = error;
                const dialogRef = this.dialog.open(AlertDialogComponent, {
                    data: {
                        title: 'ERROR',
                        message: errMsg,
                    },
                });
                this.addEvidenceForm.enable();
                this.isWorking = false;
            },
            complete: () => {
            }
        });
    }

    // Charge data to edit evidence
    edit(id: string) {
        this.evidenceId = id;
        // clear file control before resetting form to avoid wrong type errors
        this.fileControl.clear();
        this.addEvidenceForm.reset({
            label: '',
            order: '',
            file: [] // FileUploadControl expects an array of File
        });
        const evidence = this.dataSource.data.find(
            ({ id }) => id === this.evidenceId
        );
        if (evidence) {
            this.addEvidenceForm.patchValue({ label: evidence.label });
            if (evidence.order) {
                this.addEvidenceForm.patchValue({ order: evidence.order });
            }
        }
        this.addEvidenceForm.enable();
        this.classApplied = !this.classApplied;
    }

    // Delete Evidence
    delete(id: string) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                action: 'delete',
                component: 'evidence',
                content: 'content-delete',
                buttonText: {
                    ok: 'yes',
                    cancel: 'no',
                },
            },
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
                console.log('Delete Evidence: ' + id);
                this.apiService.deleteEvidence(id).subscribe({
                    next: (value: EvidenceResponse) => {
                        console.log(value);
                        this.apiService
                            .getEvidenceByVulnerabilityResult(
                                this.vulnerabilityAssessmentResultId,
                                this.paginator.pageIndex,
                                this.paginator.pageSize,
                                this.sort.active,
                                this.sort.direction,
                                this.roleName != GlobalConstants.internal ? true : false
                            )
                            .subscribe((response: EvidenceResponse) => {
                                // Flip flag to show that loading has finished.
                                this.isLoadingResults = false;
                                if (response) {
                                    this.totalItems = response.totalElements;
                                    this.dataSource.data = response.evidences;
                                } else {
                                    this.totalItems = 0;
                                    this.dataSource.data = [];
                                }
                            });
                    },
                    error: (error) => {
                        let errMsg: string = error;
                        const dialogRef = this.dialog.open(
                            AlertDialogComponent,
                            {
                                data: {
                                    title: 'ERROR',
                                    message: errMsg,
                                },
                            }
                        );
                    },
                });
            }
        });
    }
}
